import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { AuthProvider } from "./context/AuthContext";

// Public pages
import Home from "./pages/Home";
import Developments from "./pages/Developments";
import DevelopmentDetail from "./pages/DevelopmentDetail";
import About from "./pages/About";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Contact from "./pages/Contact";
import ExplorePage from "./pages/ExplorePage";
import CategoryPage from "./pages/CategoryPage";

// Admin
import AdminLogin from "./pages/admin/AdminLogin";
import ResetPassword from "./pages/admin/ResetPassword";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminDevelopments from "./pages/admin/AdminDevelopments";
import AdminDevelopmentEdit from "./pages/admin/AdminDevelopmentEdit";
import AdminBlogList from "./pages/admin/AdminBlogList";
import AdminBlogEdit from "./pages/admin/AdminBlogEdit";
import AdminPagesList from "./pages/admin/AdminPagesList";
import AdminPageEdit from "./pages/admin/AdminPageEdit";
import AdminHomeEdit from "./pages/admin/AdminHomeEdit";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminHeaderEdit from "./pages/admin/AdminHeaderEdit";
import AdminFooterEdit from "./pages/admin/AdminFooterEdit";
import AdminSEOEdit from "./pages/admin/AdminSEOEdit";
import SeoHead from "./components/SeoHead";
import Analytics from "./components/Analytics";
import LanguagePrompt from "./components/LanguagePrompt";
import AdminTranslations from "./pages/admin/AdminTranslations";
import AdminUIStrings from "./pages/admin/AdminUIStrings";
import { I18nProvider, useI18n } from "./i18n/I18nProvider";
import { PREFIXED_LANGS } from "./i18n/core";

import "@/App.css";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function PublicChrome({ children }) {
  const { pathname } = useLocation();
  const { lang } = useI18n();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <React.Fragment key={lang}>
      <Navbar />
      <main>{children}</main>
      {!isAdmin && <Footer />}
    </React.Fragment>
  );
}

function PublicChromeWithPrompt({ children }) {
  return (
    <>
      <PublicChrome>{children}</PublicChrome>
      <LanguagePrompt />
    </>
  );
}

const PUBLIC_ROUTES = [
  ["", Home],
  ["developments", Developments],
  ["developments/:slug", DevelopmentDetail],
  ["about", About],
  ["blog", Blog],
  ["blog/:slug", BlogPost],
  ["contact", Contact],
  ["explore/:area", ExplorePage],
  ["categories/:category", CategoryPage],
];

const publicRoutes = ["", ...PREFIXED_LANGS.map((c) => `/${c}`)].flatMap((prefix) =>
  PUBLIC_ROUTES.map(([path, Page]) => {
    const full = path ? `${prefix}/${path}` : prefix || "/";
    return <Route key={full} path={full} element={<Page />} />;
  })
);

function App() {
  return (
    <div className="App font-sans" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <BrowserRouter>
        <AuthProvider>
          <I18nProvider>
          <ScrollToTop />
          <SeoHead />
          <Analytics />
          <PublicChromeWithPrompt>
            <Routes>
              {/* Public (English at root, other languages prefixed e.g. /es/...) */}
              {publicRoutes}

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="developments" element={<AdminDevelopments />} />
                <Route path="developments/new" element={<AdminDevelopmentEdit mode="new" />} />
                <Route path="developments/:slug/edit" element={<AdminDevelopmentEdit mode="edit" />} />
                <Route path="blog" element={<AdminBlogList />} />
                <Route path="blog/new" element={<AdminBlogEdit mode="new" />} />
                <Route path="blog/:slug/edit" element={<AdminBlogEdit mode="edit" />} />
                <Route path="pages" element={<AdminPagesList />} />
                <Route path="pages/:key" element={<AdminPageEdit />} />
                <Route path="home" element={<AdminHomeEdit />} />
                <Route path="header" element={<AdminHeaderEdit />} />
                <Route path="footer" element={<AdminFooterEdit />} />
                <Route path="seo" element={<AdminSEOEdit />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="translations" element={<AdminTranslations />} />
                <Route path="ui-strings" element={<AdminUIStrings />} />
              </Route>

              <Route path="*" element={<Home />} />
            </Routes>
          </PublicChromeWithPrompt>
          <Toaster
            position="bottom-center"
            toastOptions={{
              classNames: {
                toast: "bg-[#1A1A1A] text-[#F9F6F0] border-0 rounded-none font-light tracking-wide",
                description: "text-[#A8A39A]",
              },
            }}
          />
          </I18nProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
